import type { PublicEvent } from "@vzlive/shared";

export interface EmbedOptions {
  origin: string;
  event?: Pick<PublicEvent, "id" | "slug" | "title"> | null;
  theme?: "auto" | "light" | "dark";
  showBrand?: boolean;
  minHeight?: number;
  initialHeight?: number;
  source?: "current" | "archive";
}

/**
 * Picks the event that an "always current" embed should display:
 * live first, then the next scheduled event, then the most recent archive.
 */
export function resolveCurrentEvent(events: PublicEvent[]): PublicEvent | null {
  const byStart = (event: PublicEvent) => Date.parse(event.startsAt);
  const live = events
    .filter((event) => event.status === "live")
    .sort((first, second) => byStart(first) - byStart(second))[0];
  if (live) return live;
  const now = Date.now();
  const next = events
    .filter((event) => event.status === "scheduled" && byStart(event) >= now)
    .sort((first, second) => byStart(first) - byStart(second))[0];
  if (next) return next;
  const paused = events
    .filter(
      (event) =>
        event.status === "paused" &&
        (!event.endsAt || Date.parse(event.endsAt) >= now),
    )
    .sort((first, second) => byStart(second) - byStart(first))[0];
  if (paused) return paused;
  const archived = events
    .filter((event) => event.status === "archived")
    .sort((first, second) => byStart(second) - byStart(first))[0];
  return archived ?? null;
}

const esc = (value: string) =>
  value.replace(
    /[&<>"']/g,
    (character) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#039;",
      })[character]!,
  );

const frameId = (event: EmbedOptions["event"]) =>
  `vzlive-${(event?.id ?? "current").replace(/[^a-zA-Z0-9_-]/g, "") || "current"}`;

const iframeTag = ({
  origin,
  path,
  title,
  id,
  withId,
  height,
}: {
  origin: string;
  path: string;
  title: string;
  id: string;
  withId: boolean;
  height: number;
}) =>
  `<iframe${withId ? ` id="${esc(id)}"` : ""} src="${esc(
    `${origin.replace(/\/$/, "")}${path}`,
  )}" title="${esc(title)}" loading="lazy" style="display:block;width:100%;height:${height}px;border:0" allow="clipboard-write; autoplay"></iframe>`;

/** Frame path for the selected embed source. */
export function embedPath(
  source: EmbedOptions["source"],
  event: EmbedOptions["event"],
): string {
  if (source === "archive") return "/embed/archive";
  if (source === "current" || !event) return "/embed/current";
  return `/embed/${event.slug}`;
}

const snippetTitle = (
  source: EmbedOptions["source"],
  event: EmbedOptions["event"],
) =>
  source === "archive"
    ? "Archiv Live-Ticker"
    : `${event?.title ?? "Live-Ticker"} Live-Ticker`;

/** Frames at a fixed height scroll internally. No script needed. */
export function buildIframeSnippet(options: EmbedOptions): string {
  const { origin, event, source, initialHeight = 640 } = options;
  const id = frameId(event);
  return iframeTag({
    origin,
    path: embedPath(source, event),
    title: snippetTitle(source, event),
    id,
    withId: false,
    height: initialHeight,
  });
}

/**
 * Auto-height iframe: the embed page reports its scrollHeight via
 * postMessage and the host grows the frame, so the host page scrolls.
 * The embed origin is allow-listed and passed as target origin.
 */
export function buildAutoIframeSnippet(options: EmbedOptions): string {
  const {
    origin,
    event,
    source,
    minHeight = 320,
    initialHeight = 640,
  } = options;
  const widgetOrigin = origin.replace(/\/$/, "");
  const id = frameId(event);
  const src = `${widgetOrigin}${embedPath(source, event)}?parent=${encodeURIComponent(origin)}`;
  return [
    `<iframe id="${esc(id)}" src="${esc(src)}" title="${esc(
      snippetTitle(source, event),
    )}" loading="lazy" style="display:block;width:100%;height:${initialHeight}px;border:0" allow="clipboard-write; autoplay"></iframe>`,
    `<script>(function(){var f=document.getElementById("${esc(id)}");if(!f)return;var origin="${esc(widgetOrigin)}";addEventListener("message",function(e){if(e.origin!==origin||!e.source||e.source!==f.contentWindow)return;var d=e.data;if(!d||d.type!=="vzlive:height")return;var h=Number(d.height);if(h>0)f.style.height=Math.max(${Math.round(minHeight)},Math.round(h))+"px"})})()</script>`,
  ].join("\n");
}

/**
 * Native custom element: inherits the host page's typography.
 * The loader only *defines* <vz-live-ticker> – the element itself must sit
 * where the ticker should appear, so the snippet emits both.
 *
 * The loader URL is intentionally unversioned (/vzlive.js, 5 min cache):
 * host pages never update their snippet, so a build-id-pinned URL would
 * hand out a response that browsers cache as immutable for a year under
 * a URL that never changes – later deploys would never reach those hosts.
 */
export function buildWidgetSnippet(options: EmbedOptions): string {
  const { origin, event, source, theme = "auto", showBrand = true } = options;
  const widgetOrigin = origin.replace(/\/$/, "");
  const mode = source === "archive" ? "archive" : (event?.slug ?? "current");
  const element = `<vz-live-ticker data-event="${esc(
    mode,
  )}" data-theme="${esc(theme)}" data-brand="${
    showBrand ? "show" : "hide"
  }"></vz-live-ticker>`;
  return `${element}\n<script async src="${esc(
    `${widgetOrigin}/vzlive.js`,
  )}"></script>`;
}
